// bluaIQ — the STABLE machine codes for contract-signing rejections.
//
// WHY THIS MODULE EXISTS (Codex, #479 P1). The signing page used to classify
// failures by matching the server's English prose (`/expired/i` over the
// message). That is the same anti-pattern as the engine-jargon leak: it couples
// a user-facing decision to text that exists for humans, so a harmless reword on
// the server silently reroutes the contractor to the wrong screen.
//
// The cure is a code both sides IMPORT rather than a string one side guesses.
// This module is the single source of truth: the server emits these values and
// the client switches on them. Neither side owns the vocabulary alone, and a
// rename is a compile-time event instead of a silent behavioral change.
//
// The prose `error` field stays on the wire for operators and existing tests —
// it is a log line, never a routing input and never user copy.

export const SIGN_ERROR_CODES = {
  /** Wrong token, unknown contract, or a token already consumed by a signature.
   *  Deliberately ONE code: the three are indistinguishable to the caller so the
   *  endpoint cannot be used to enumerate contract ids. */
  INVALID: 'sign_token_invalid',
  /** The signing link is past its 30-day TTL. */
  EXPIRED: 'sign_link_expired',
  /** The contract already carries a signature (replay, or the loser of a
   *  concurrent-signature race). */
  ALREADY_SIGNED: 'contract_already_signed',
} as const

export type SignErrorCode = (typeof SIGN_ERROR_CODES)[keyof typeof SIGN_ERROR_CODES]

/** Every code, for exhaustiveness checks on both sides. */
export const ALL_SIGN_ERROR_CODES: readonly SignErrorCode[] = Object.values(SIGN_ERROR_CODES)

/** True when `v` is a code this build knows. Unknown codes (a newer server
 *  talking to an older client during a rolling deploy) must degrade to the
 *  curated generic rather than be trusted. */
export function isSignErrorCode(v: unknown): v is SignErrorCode {
  return typeof v === 'string' && (ALL_SIGN_ERROR_CODES as readonly string[]).includes(v)
}
